import { ComponentBase } from '../../common/component-base';
import { CustomerMasterData } from '../../common/model/customer/customer-master-data';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, computed, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CustomerService } from '../../common/service/customer.service';
import { NotificationService } from '../../common/service/notification.service';
import { UserService } from '../../common/service/user.service';
import { SalutationPipe } from '../../common/pipes/salutation.pipe';
import {
  Contact,
  ContactFunction,
  ContactFunctionCategory,
  ContactFunctionUpdate,
  MasterDataCompanySettings,
} from './model/contact';
import { FunctionCategory, normalizeFunctionCategory } from './enum/function-category.enum';
import { FunctionCategoryPipe } from './pipes/function-category.pipe';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { Table, TableFilterEvent, TableModule, TablePageEvent } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TableFilterIconComponent } from '../../common/component/table-filter-icon/table-filter-icon.component';
import {
  GenericTableSkeletonComponent,
  SkeletonTableColumn,
} from '../../common/component/skeleton-loader/generic-table-skeleton/generic-table-skeleton.component';
import { Role } from '../../core/auth/role.enum';
import { clearTableSessionState, getTableSearchValue, getTableSessionState, persistTableSessionStatePatch, setTableSearchValue, TablePersistenceConfig } from '../../common/helper/table-session-state';
import { TablePersistenceDirective } from '../../common/directive/table-persistence.directive';
import { CustomerLogoSettingsComponent } from './customer-logo-settings/customer-logo-settings.component';

interface ContactFunctionFilterOption extends Record<string, unknown> {
  category: FunctionCategory | string;
  key: string;
  value: string;
}

@Component({
  selector: 'gate-master-data',
  standalone: true,
  imports: [ButtonModule,
    DialogModule,
    FieldsetModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MultiSelectModule,
    NgTemplateOutlet,
    SelectModule,
    Skeleton,
    TableModule,
    TableFilterIconComponent,
    ToggleSwitchModule,
    TooltipModule,
    SalutationPipe,
    FunctionCategoryPipe,
    GenericTableSkeletonComponent,
    TranslatePipe,
    TablePersistenceDirective,
    CustomerLogoSettingsComponent,
  ],
  templateUrl: './master-data.component.html',
  styleUrl: './master-data.component.scss',
})
export class MasterDataComponent extends ComponentBase implements OnDestroy {
  public readonly Role = Role;
  private readonly tableStateKey = 'masterDataTableState';
  private readonly tablePageStateKey = `${this.tableStateKey}:page`;
  public readonly tablePersistence: TablePersistenceConfig = { persistPage: false };

  private readonly assignedFunctionsOnlyStateKey = `${this.tableStateKey}:assignedFunctionsOnly`;
  private isApplyingRestoredPage = false;
  private tablePageRestoreTimeouts: ReturnType<typeof setTimeout>[] = [];
  private isUserPaginatorInteraction = false;
  private removePaginatorClickListener: (() => void) | undefined;
  private removePaginatorKeydownListener: (() => void) | undefined;

  private customerService = inject(CustomerService);
  private notificationService = inject(NotificationService);
  private userService = inject(UserService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef<HTMLElement>);

  public contacts = signal<Contact[]>([]);
  public currentPageFirst = signal(0);
  public searchValue = signal('');
  public customerMasterData: CustomerMasterData | null = null;
  public customerFullName = signal('');
  public contactFunctionCategories: ContactFunctionCategory[] = [];
  public contactFunctionFilterOptions: ContactFunctionFilterOption[] = [];
  public editDialogVisible = false;
  public selectedContact?: Contact;
  public editFunctionSelection: Record<string, boolean> = {};
  public isSavingContactFunctions = false;
  public isMasterDataLoading = signal(false);
  public masterDataLoaded = false;
  public isContactsLoading = signal(false);
  public contactsLoaded = false;
  public itemsPerPage = 10;
  public assignedFunctionsOnly = signal(false);
  private hasColumnFilter = false;
  @ViewChild('masterDataTable') private masterDataTable?: Table;
  public readonly contactTableSkeletonColumns: SkeletonTableColumn[] = [
    { headerWidth: '6rem', cellWidth: '70%', width: '24%' },
    { headerWidth: '5rem', cellWidth: '80%', width: '30%' },
    { headerWidth: '10rem', cellWidth: '14rem', cellHeight: '2rem', width: '36%' },
  ];

  // ACP-level access applies when the caller is genuinely ACP staff (AccountManager role), or when
  // an ACP user is impersonating a company generically (no specific person picked). It must NOT apply
  // when an ACP user is impersonating a specific person - that view should be scoped to exactly what
  // that person is allowed to do, per userService.currentUser().roles already reflecting their own roles.
  public isAcpUser = computed(() =>
    this.userService.currentUser().roles.includes(Role.AccountManager) || this.userService.isCompanyImpersonatedByAcpUser(),
  );
  private hasMasterDataWrite = computed(() => this.userService.getRoles('both').includes(Role.GateMasterDataWrite));
  public canEditAll = computed(() =>
    this.isAcpUser() || this.userService.getRoles('both').includes(Role.GateMasterDataWriteAll),
  );

  public companySettings = signal<MasterDataCompanySettings | null>(null);
  public isCompanySettingsLoading = signal(false);
  public isSavingElectronicInvoiceRecipient = signal(false);
  public isSavingReminderRecipients = signal(false);
  public selectedInvoiceRecipientContactNumber: number | null = null;
  public selectedInvoiceFunctionSelection: Record<string, boolean> = {};
  public selectedReminderRecipientContactNumbers: number[] = [];

  // Same visibility rule the former "Kundeneinstellungen" menu entry used.
  public canManageCustomerLogo = computed(() => {
    const user = this.userService.currentUser();
    if (this.userService.isImpersonatedByAcpUser()) {
      return !!this.userService.getCustomerNumber();
    }
    return !!user?.roles?.includes(Role.Customer) && (user?.assignedCustomers?.length ?? 0) > 0;
  });

  public get summaryContact(): Contact | undefined {
    const contacts = this.contacts();
    return contacts.find((contact) => contact.contactNumber === 0) ?? contacts[0];
  }

  public get listedContacts(): Contact[] {
    return this.contacts().filter((contact) => contact.contactNumber !== 0);
  }

  /** Categories editable per-contact in the "Funktionen bearbeiten" dialog (A only - B/C moved to company-wide settings). */
  public get dialogFunctionCategories(): ContactFunctionCategory[] {
    return this.contactFunctionCategories.filter((category) => {
      const value = normalizeFunctionCategory(this.getCategoryValue(category));
      return value !== FunctionCategory.ElectronicInvoice && value !== FunctionCategory.Reminder;
    });
  }

  public get electronicInvoiceCategory(): ContactFunctionCategory | undefined {
    return this.contactFunctionCategories.find(
      (category) => normalizeFunctionCategory(this.getCategoryValue(category)) === FunctionCategory.ElectronicInvoice,
    );
  }

  public filteredContacts = computed(() => {
    const contacts = this.contacts();
    const searchTerm = this.searchValue().trim().toLowerCase();

    const listed = contacts.filter((contact) => contact.contactNumber !== 0);

    const afterToggle = this.assignedFunctionsOnly()
      ? listed.filter((contact) => this.hasAssignedFunctions(contact))
      : listed;

    const filtered = searchTerm
      ? afterToggle.filter((contact) => this.getContactSearchText(contact).includes(searchTerm))
      : afterToggle;

    return [...filtered].sort((a, b) => (a.sortName ?? '').localeCompare(b.sortName ?? ''));
  });

  constructor() {
    super();
    this.restoreTableState();
    void this.getCustomerMasterData();
    void this.loadCustomerFullName();
    void this.getAllContacts();
    // Company settings selection depends on the function categories being loaded first.
    void this.getAllAvailableContactFunctions().then(() => this.loadMasterDataCompanySettings());
  }

  ngOnDestroy(): void {
    this.tablePageRestoreTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.removePaginatorClickListener?.();
    this.removePaginatorKeydownListener?.();
  }

  private async loadCustomerFullName() {
    try {
      var customerData = await this.customerService.getCustomerData();
      if (customerData) {
        this.customerFullName.set(customerData.fullName);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
    }
  }

  private async getCustomerMasterData() {
    this.isMasterDataLoading.set(true);
    this.masterDataLoaded = false;

    try {
      this.customerMasterData = await this.customerService.getCustomerMasterData();
    } catch (error) {
      console.error('Error fetching get customer data:', error);
    } finally {
      this.masterDataLoaded = true;
      this.isMasterDataLoading.set(false);
    }
  }

  private async getAllContacts() {
    this.isContactsLoading.set(true);
    this.contactsLoaded = false;

    try {
      const contacts = await this.customerService.getContacts();
      this.contacts.set(
        contacts
          .filter((contact) => this.isActiveContact(contact))
          .map((contact) => this.withContactFunctionKeys(contact)),
      );
      this.restoreTablePageForCurrentResult();
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      this.contactsLoaded = true;
      this.isContactsLoading.set(false);
    }
  }

  private async getAllAvailableContactFunctions() {
    try {
      const contactFunctionCategories = await this.customerService.getAllAvailableContactFunctions();
      this.contactFunctionCategories = this.sortContactFunctionCategories(contactFunctionCategories);
      this.contactFunctionFilterOptions = this.getContactFunctionFilterOptions(this.contactFunctionCategories);
    } catch (error) {
      console.error('Error fetching contact functions:', error);
    }
  }

  public getActiveFunctionCategories(contact: Contact): ContactFunctionCategory[] {
    const contactFunctionKeys = new Set(contact.functions.map((contactFunction) => this.getFunctionKey(contactFunction)));

    return this.contactFunctionCategories
      .map((category) => ({
        ...category,
        functions: category.functions.filter((categoryFunction) =>
          contactFunctionKeys.has(this.getFunctionKey(categoryFunction)),
        ),
      }))
      .filter((category) => category.functions.length > 0);
  }

  public isOwnContact(contact: Contact): boolean {
    const email = this.userService.currentUser().email;
    return !!email && !!contact.emailAddress && contact.emailAddress.toLowerCase() === email.toLowerCase();
  }

  public canEditContact(contact: Contact): boolean {
    return this.canEditAll() || (this.hasMasterDataWrite() && this.isOwnContact(contact));
  }

  private async loadMasterDataCompanySettings() {
    this.isCompanySettingsLoading.set(true);

    try {
      const settings = await this.customerService.getMasterDataCompanySettings();
      this.companySettings.set(settings);
      this.selectedInvoiceRecipientContactNumber = settings.electronicInvoiceRecipientContactNumber;
      this.selectedInvoiceFunctionSelection = Object.fromEntries(
        this.getElectronicInvoiceFunctions().map((fn) => [
          this.getFunctionKey(fn),
          settings.electronicInvoiceFunctionCodes.includes(fn.fugrCd),
        ]),
      );
      this.selectedReminderRecipientContactNumbers = [...settings.reminderRecipientContactNumbers];
    } catch (error) {
      console.error('Error fetching master data company settings:', error);
    } finally {
      this.isCompanySettingsLoading.set(false);
    }
  }

  public getContactDisplayNameByContactNumber(contactNumber: number | null | undefined): string {
    if (contactNumber == null) {
      return '';
    }

    const contact = this.contacts().find((c) => c.contactNumber === contactNumber);
    return contact ? this.getContactName(contact) : '';
  }

  public getElectronicInvoiceFunctions(): ContactFunction[] {
    return this.electronicInvoiceCategory?.functions ?? [];
  }

  public isInvoiceFunctionSelected(contactFunction: ContactFunction): boolean {
    return this.selectedInvoiceFunctionSelection[this.getFunctionKey(contactFunction)] ?? false;
  }

  public setInvoiceFunctionSelected(contactFunction: ContactFunction, selected: boolean) {
    this.selectedInvoiceFunctionSelection[this.getFunctionKey(contactFunction)] = selected;
  }

  public onInvoiceRecipientChange(contactNumber: number | null) {
    this.selectedInvoiceRecipientContactNumber = contactNumber;

    const settings = this.companySettings();
    const currentCodes = contactNumber === settings?.electronicInvoiceRecipientContactNumber
      ? settings.electronicInvoiceFunctionCodes
      : [];

    this.selectedInvoiceFunctionSelection = Object.fromEntries(
      this.getElectronicInvoiceFunctions().map((fn) => [this.getFunctionKey(fn), currentCodes.includes(fn.fugrCd)]),
    );
  }

  public async saveElectronicInvoiceRecipient() {
    if (this.selectedInvoiceRecipientContactNumber == null || this.isSavingElectronicInvoiceRecipient()) {
      return;
    }

    const customerNumber = this.summaryContact?.customerNumber ?? this.userService.getCustomerNumber() ?? '';
    const functionCodes = this.getElectronicInvoiceFunctions()
      .filter((fn) => this.isInvoiceFunctionSelected(fn))
      .map((fn) => fn.fugrCd);

    this.isSavingElectronicInvoiceRecipient.set(true);

    try {
      await this.customerService.updateElectronicInvoiceRecipient({
        customerNumber,
        contactNumber: this.selectedInvoiceRecipientContactNumber,
        functionCodes,
      });

      await Promise.all([this.loadMasterDataCompanySettings(), this.getAllContacts()]);
      this.notificationService.showNotification(
        'success',
        'notification.masterData.companySettings.success.title',
        'notification.masterData.companySettings.success.message',
      );
    } catch (error) {
      console.error('Error updating electronic invoice recipient:', error);
      this.notificationService.showNotification(
        'error',
        'notification.masterData.companySettings.error.title',
        'notification.masterData.companySettings.error.message',
      );
    } finally {
      this.isSavingElectronicInvoiceRecipient.set(false);
    }
  }

  public async saveReminderRecipients() {
    if (this.isSavingReminderRecipients()) {
      return;
    }

    const customerNumber = this.summaryContact?.customerNumber ?? this.userService.getCustomerNumber() ?? '';

    this.isSavingReminderRecipients.set(true);

    try {
      await this.customerService.updateReminderRecipients({
        customerNumber,
        contactNumbers: this.selectedReminderRecipientContactNumbers,
      });

      await Promise.all([this.loadMasterDataCompanySettings(), this.getAllContacts()]);
      this.notificationService.showNotification(
        'success',
        'notification.masterData.companySettings.success.title',
        'notification.masterData.companySettings.success.message',
      );
    } catch (error) {
      console.error('Error updating reminder recipients:', error);
      this.notificationService.showNotification(
        'error',
        'notification.masterData.companySettings.error.title',
        'notification.masterData.companySettings.error.message',
      );
    } finally {
      this.isSavingReminderRecipients.set(false);
    }
  }

  public openEditDialog(contact: Contact) {
    this.selectedContact = contact;
    const contactFunctionKeys = new Set(contact.functions.map((contactFunction) => this.getFunctionKey(contactFunction)));

    this.editFunctionSelection = Object.fromEntries(
      this.getAvailableContactFunctions().map((contactFunction) => {
        const functionKey = this.getFunctionKey(contactFunction);

        return [functionKey, contactFunctionKeys.has(functionKey)];
      }),
    );

    this.editDialogVisible = true;
  }

  public closeEditDialog() {
    if (this.isSavingContactFunctions) {
      return;
    }

    this.editDialogVisible = false;
    this.selectedContact = undefined;
    this.editFunctionSelection = {};
  }

  public isFunctionSelected(contactFunction: ContactFunction): boolean {
    return this.editFunctionSelection[this.getFunctionKey(contactFunction)] ?? false;
  }

  public setFunctionSelected(contactFunction: ContactFunction, selected: boolean) {
    if (this.isSavingContactFunctions) {
      return;
    }

    this.editFunctionSelection[this.getFunctionKey(contactFunction)] = selected;
  }

  public saveContactFunctions() {
    if (!this.selectedContact || this.isSavingContactFunctions) {
      return;
    }

    const selectedContact = this.selectedContact;
    const selectedFunctions = this.getSelectedContactFunctions();
    // Only diff against the dialog-editable (category A) functions the contact currently has -
    // company-wide functions (ElectronicInvoice/Reminder) aren't offered here and must not be
    // treated as removed just because this dialog doesn't present them.
    const editableKeys = new Set(this.getAvailableContactFunctions().map((fn) => this.getFunctionKey(fn)));
    const editableAssignedFunctions = selectedContact.functions.filter((fn) => editableKeys.has(this.getFunctionKey(fn)));
    const functionUpdates = this.getFunctionUpdates(editableAssignedFunctions, selectedFunctions);

    if (!functionUpdates.length) {
      this.closeEditDialog();
      return;
    }

    this.isSavingContactFunctions = true;
    this.customerService
      .updateContactFunctions({
        customerNumber: selectedContact.customerNumber,
        contactNumber: selectedContact.contactNumber,
        functions: functionUpdates,
      })
      .then(() => {
        this.contacts.update((contacts) =>
          contacts.map((contact) => {
            if (contact.id !== selectedContact.id) {
              return contact;
            }

            // Keep company-wide (ElectronicInvoice/Reminder) functions untouched - this dialog
            // only edits category A, so only replace the dialog-editable functions locally.
            const unmanagedFunctions = contact.functions.filter((fn) => !editableKeys.has(this.getFunctionKey(fn)));
            const updatedFunctions = [...unmanagedFunctions, ...selectedFunctions];

            return {
              ...contact,
              functions: updatedFunctions,
              contactFunctionKeys: this.getContactFunctionKeys(updatedFunctions),
            };
          }),
        );

        this.isSavingContactFunctions = false;
        this.closeEditDialog();
        this.notificationService.showNotification(
          'success',
          'notification.masterData.contactFunctions.success.title',
          'notification.masterData.contactFunctions.success.message',
        );
      })
      .catch((error) => {
        this.isSavingContactFunctions = false;
        this.notificationService.showNotification(
          'error',
          'notification.masterData.contactFunctions.error.title',
          'notification.masterData.contactFunctions.error.message',
        );
        console.error('Error updating contact functions:', error);
      });
  }

  public getFunctionKey(contactFunction: ContactFunction): string {
    return `${contactFunction.fugaCd}:${contactFunction.fugrCd}`;
  }

  public getCategoryValue(category: ContactFunctionCategory): FunctionCategory | string {
    return category.category ?? category.categoryNameKey ?? '';
  }

  public getFunctionDisplayName(contactFunction: ContactFunction): string {
    return contactFunction.description?.trim() || contactFunction.fugrCd;
  }

  public getFunctionCodes(category: ContactFunctionCategory): string {
    return category.functions.map((contactFunction) => this.getFunctionDisplayName(contactFunction)).join(', ');
  }

  public getContactName(contact: Contact): string {
    return `${contact.firstName} ${contact.lastName}`.trim();
  }

  public getContactDisplayName(contact: Contact, salutation = ''): string {
    return [salutation, this.getContactName(contact)].filter(Boolean).join(' ');
  }

  public getPaymentTermsDaysNet(): number | null {
    const daysNet = this.customerMasterData?.customer?.daysNet;

    return typeof daysNet === 'number' ? daysNet : null;
  }

  public shouldShowSummaryCard(): boolean {
    return !!this.summaryContact || !!this.customerMasterData?.customer;
  }

  public getSummaryAddressLines(): string[] {
    if (!this.summaryContact) {
      return [];
    }

    return [
      this.summaryContact.mainAddress.streetAddress,
      `${this.summaryContact.mainAddress.postCode} ${this.summaryContact.mainAddress.postTown}`.trim(),
      this.summaryContact.mainAddress.country,
    ].filter(Boolean);
  }

  public getCustomerNumber(): string {
    return this.summaryContact?.customerNumber ?? this.userService.getCustomerNumber() ?? '-';
  }

  public getMandant(): string {
    return this.customerMasterData?.customer?.mandant?.trim() || '-';
  }

  public getPrimaryContactLines(): string[] {
    if (!this.summaryContact) {
      return [];
    }

    return [
      this.summaryContact.telephoneNumber,
      this.summaryContact.mobileNumber,
      this.summaryContact.notifyAddress || this.summaryContact.emailAddress,
    ].filter(Boolean);
  }

  public onPageChange(event: TablePageEvent): void {
    if (this.isApplyingRestoredPage) {
      return;
    }

    const savedFirst = this.getPersistedTablePageState()?.first ?? 0;

    if (event.first === 0 && this.currentPageFirst() > 0 && savedFirst > 0 && !this.isUserPaginatorInteraction) {
      this.restoreTablePageForCurrentResult();
      return;
    }

    this.currentPageFirst.set(event.first);
    this.itemsPerPage = event.rows;
    persistTableSessionStatePatch(this.tablePageStateKey, {
      first: event.first,
      rows: event.rows,
    });
    this.isUserPaginatorInteraction = false;
  }

  public onSearchChange(value: string): void {
    this.searchValue.set(value);
    setTableSearchValue(this.tableStateKey, value);
    this.resetTablePageToFirst();
  }

  // Whether the search box has a value, the toggle is on, or a column filter is active.
  public hasActiveFilterOrSearch(): boolean {
    return !!this.searchValue()?.trim() || this.assignedFunctionsOnly() || this.hasColumnFilter;
  }

  /**
   * Tracks whether the assigned-functions column filter has an actual value applied.
   * PrimeNG registers a filter entry for the p-columnFilter on init with value null,
   * so presence of a key in table.filters alone doesn't mean it's active.
   */
  public onFilter(event: TableFilterEvent): void {
    this.hasColumnFilter = Object.entries(event.filters ?? {}).some(([field, meta]) => {
      if (field === 'global') {
        return false;
      }
      const value = Array.isArray(meta) ? meta[0]?.value : (meta as { value?: unknown })?.value;
      return value !== null && value !== undefined && value !== '';
    });
  }

  public clearFilter(table: Table) {
    clearTableSessionState(this.tableStateKey);
    clearTableSessionState(this.tablePageStateKey);
    this.searchValue.set('');
    this.assignedFunctionsOnly.set(false);
    this.currentPageFirst.set(0);
    this.hasColumnFilter = false;
    sessionStorage.removeItem(this.assignedFunctionsOnlyStateKey);
    table.reset();
  }

  public onAssignedFunctionsOnlyToggle(checked: boolean): void {
    this.assignedFunctionsOnly.set(checked);
    if (checked) {
      sessionStorage.setItem(this.assignedFunctionsOnlyStateKey, 'true');
    } else {
      sessionStorage.removeItem(this.assignedFunctionsOnlyStateKey);
    }
    this.resetTablePageToFirst();
  }

  private resetTablePageToFirst(): void {
    this.currentPageFirst.set(0);
    if (this.masterDataTable) {
      this.masterDataTable.first = 0;
      this.masterDataTable.firstChange.emit(0);
    }
    persistTableSessionStatePatch(this.tablePageStateKey, {
      first: 0,
      rows: this.itemsPerPage,
    });
    this.changeDetectorRef.detectChanges();
  }

  private restoreTableState(): void {
    const restoredState = this.getPersistedTablePageState();

    if (restoredState?.first != null) {
      this.currentPageFirst.set(restoredState.first);
    }

    if (restoredState?.rows != null) {
      this.itemsPerPage = restoredState.rows;
    }

    this.searchValue.set(getTableSearchValue(this.tableStateKey));
    this.assignedFunctionsOnly.set(sessionStorage.getItem(this.assignedFunctionsOnlyStateKey) === 'true');
  }

  private restoreTablePageForCurrentResult(): void {
    const state = this.getPersistedTablePageState();
    const rows = state?.rows ?? this.itemsPerPage;
    const first = state?.first ?? this.currentPageFirst();
    const totalRecords = this.filteredContacts().length;
    const validFirst = totalRecords > 0 && first >= totalRecords
      ? Math.max(Math.floor((totalRecords - 1) / rows) * rows, 0)
      : first;

    this.itemsPerPage = rows;
    this.currentPageFirst.set(validFirst);
    this.applyRestoredPageToPrimeTable(validFirst, rows);
  }

  private applyRestoredPageToPrimeTable(first: number, rows: number): void {
    this.bindPaginatorInteractionListeners();
    this.tablePageRestoreTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.tablePageRestoreTimeouts = [
      setTimeout(() => {
        this.applyPageToTable(first, rows);
      }),
    ];
  }

  private applyPageToTable(first: number, rows: number): void {
    const table = this.masterDataTable;

    if (!table) {
      return;
    }

    this.isApplyingRestoredPage = true;
    table.onPageChange({
      first,
      rows,
      page: Math.floor(first / rows),
      pageCount: Math.ceil(this.filteredContacts().length / rows),
    } as TablePageEvent);
    table.first = first;
    table.rows = rows;
    this.currentPageFirst.set(first);
    this.itemsPerPage = rows;
    persistTableSessionStatePatch(this.tablePageStateKey, { first, rows });
    this.changeDetectorRef.detectChanges();

    setTimeout(() => {
      this.isApplyingRestoredPage = false;
    });
  }

  private getPersistedTablePageState() {
    return getTableSessionState(this.tablePageStateKey) ?? getTableSessionState(this.tableStateKey);
  }

  private bindPaginatorInteractionListeners(): void {
    if (this.removePaginatorClickListener || this.removePaginatorKeydownListener) {
      return;
    }

    const nativeElement = this.elementRef.nativeElement;
    const markPaginatorInteraction = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.p-paginator')) {
        this.isUserPaginatorInteraction = true;
      }
    };

    nativeElement.addEventListener('click', markPaginatorInteraction, true);
    nativeElement.addEventListener('keydown', markPaginatorInteraction, true);

    this.removePaginatorClickListener = () => nativeElement.removeEventListener('click', markPaginatorInteraction, true);
    this.removePaginatorKeydownListener = () => nativeElement.removeEventListener('keydown', markPaginatorInteraction, true);
  }

  private isActiveContact(contact: Contact): boolean {
    const active = contact.active;

    if (active === undefined || active === null) {
      return true;
    }

    if (typeof active === 'boolean') {
      return active;
    }

    return active.toUpperCase() === 'Y';
  }

  private withContactFunctionKeys(contact: Contact): Contact {
    return {
      ...contact,
      contactFunctionKeys: this.getContactFunctionKeys(contact.functions),
      sortName: this.getContactName(contact),
      recipientLabel: this.getRecipientLabel(contact),
    };
  }

  /** "Name (email)" - lets users tell recipients apart in the company-settings dropdowns. */
  private getRecipientLabel(contact: Contact): string {
    const name = this.getContactName(contact);
    const email = contact.notifyAddress || contact.emailAddress;
    return email ? `${name} (${email})` : name;
  }

  private getContactFunctionKeys(functions: ContactFunction[]): string {
    return functions.map((contactFunction) => this.getFunctionKey(contactFunction)).join('|');
  }

  private hasAssignedFunctions(contact: Contact): boolean {
    return this.getActiveFunctionCategories(contact).length > 0;
  }

  private getAvailableContactFunctions(): ContactFunction[] {
    return this.dialogFunctionCategories.flatMap((category) => category.functions);
  }

  private getSelectedContactFunctions(): ContactFunction[] {
    return this.getAvailableContactFunctions().filter((contactFunction) => this.isFunctionSelected(contactFunction));
  }

  private sortContactFunctionCategories(categories: ContactFunctionCategory[]): ContactFunctionCategory[] {
    return [...categories].sort((a, b) =>
      String(this.getCategoryValue(a)).localeCompare(String(this.getCategoryValue(b))),
    );
  }

  private getContactFunctionFilterOptions(categories: ContactFunctionCategory[]): ContactFunctionFilterOption[] {
    const options = categories.flatMap((category) =>
      category.functions.map((contactFunction) => ({
        category: this.getCategoryValue(category),
        key: this.getFunctionDisplayName(contactFunction),
        value: this.getFunctionKey(contactFunction),
      })),
    );

    return Array.from(new Map(options.map((option) => [option.value, option])).values());
  }

  private getContactSearchText(contact: Contact): string {
    return [
      contact.salutation,
      this.getContactName(contact),
      contact.telephoneNumber,
      contact.mobileNumber,
      contact.emailAddress,
      contact.notifyAddress,
      ...contact.functions.flatMap((contactFunction) => [contactFunction.fugaCd, contactFunction.fugrCd]),
      ...contact.functions.map((contactFunction) => this.getFunctionDisplayName(contactFunction)),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  private getFunctionUpdates(
    assignedFunctions: ContactFunction[],
    selectedFunctions: ContactFunction[],
  ): ContactFunctionUpdate[] {
    const assignedFunctionKeys = new Set(assignedFunctions.map((contactFunction) => this.getFunctionKey(contactFunction)));
    const selectedFunctionKeys = new Set(selectedFunctions.map((contactFunction) => this.getFunctionKey(contactFunction)));

    return [
      ...this.getChangedFunctions(selectedFunctions, assignedFunctionKeys, 'add'),
      ...this.getChangedFunctions(assignedFunctions, selectedFunctionKeys, 'remove'),
    ];
  }

  private getChangedFunctions(
    functions: ContactFunction[],
    comparisonKeys: Set<string>,
    action: ContactFunctionUpdate['action'],
  ): ContactFunctionUpdate[] {
    return functions
      .filter((contactFunction) => !comparisonKeys.has(this.getFunctionKey(contactFunction)))
      .map((contactFunction) => ({
        ...contactFunction,
        action,
      }));
  }
}
